import React from 'react'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { AlertTriangle, ArrowRight } from 'lucide-react'

/**
 * Dialog showing field-by-field diff between Odin and Xero contact data.
 * Used as a pre-invoice verification step before authorising/sending.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {function} props.onOpenChange
 * @param {Array} props.differences - Array of {field, odinValue, xeroValue}
 * @param {string} props.customerName
 * @param {string} props.branchName
 * @param {boolean} props.syncing - Whether sync is in progress
 * @param {function} props.onSyncAndProceed - Sync to Xero then proceed
 * @param {function} props.onProceedWithoutSync - Skip sync and proceed
 * @param {function} props.onCancel - Abort the operation
 */
export function XeroSyncDiffDialog({
  open,
  onOpenChange,
  differences = [],
  customerName,
  branchName,
  syncing = false,
  onSyncAndProceed,
  onProceedWithoutSync,
  onCancel,
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Contact Data Mismatch
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-muted-foreground">
              <span>{customerName}{branchName ? ` — ${branchName}` : ''} has differences between Odin and Xero.</span>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 my-2">
          {/* Header row */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 text-xs font-medium text-muted-foreground px-1">
            <span>Odin</span>
            <span></span>
            <span>Xero</span>
          </div>

          {differences.map((diff, i) => (
            <div key={i} className="rounded border p-3 bg-amber-50/50">
              <div className="text-xs font-medium text-muted-foreground mb-1.5">{diff.field}</div>
              <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                <span className="text-sm font-medium truncate" title={diff.odinValue || '(empty)'}>
                  {diff.odinValue || <span className="text-muted-foreground italic">(empty)</span>}
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-sm truncate" title={diff.xeroValue || '(empty)'}>
                  {diff.xeroValue || <span className="text-muted-foreground italic">(empty)</span>}
                </span>
              </div>
            </div>
          ))}
        </div>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel onClick={onCancel} disabled={syncing}>
            Cancel
          </AlertDialogCancel>
          <Button
            variant="outline"
            onClick={onProceedWithoutSync}
            disabled={syncing}
          >
            Proceed Without Syncing
          </Button>
          <Button
            onClick={onSyncAndProceed}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync to Xero & Proceed'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
